import {useHydrationDone} from '@aboutyou/lazy-imports';
import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {hideScrollBar, touchScroll} from '../../../constants/scrolling';
import {GLOBAL_KEYFRAMES} from '../../atoms/BaseStyles/keyFrames';
import {
  ProgressBarContainer,
  SliderProgressBar,
} from '../../atoms/SliderProgressBar/SliderProgressBar';
import {useSlider} from '../../molecules/HorizontalSlider/hooks/useSlider';

const EXTENDED_FIRST_CARD_TIMEOUT = 18;

interface CardsSliderAmbientProps<Card> {
  className?: string;
  cards: Card[];
  extendFirstCardTimeout?: boolean;
  CardComponent: React.ComponentType<
    Card & {
      isActive: boolean;
      isCloseToViewport: boolean;
      sectionIndex: number;
      ['data-testid']: string;
    }
  >;
  sectionIndex: number;
}

export const CardsSliderAmbient = <Card extends {duration: {seconds: number}}>({
  className,
  cards,
  CardComponent,
  sectionIndex,
  extendFirstCardTimeout,
}: CardsSliderAmbientProps<Card>) => {
  const sliderRef = useRef<HTMLUListElement>(null);
  const {currentSlide, scrollToSlide} = useSlider(sliderRef);
  const isInitialMount = !useHydrationDone();
  const [transitionSlideId, setTransitionSlideId] = useState<number>(-1);
  const [animationPaused, setAnimationPaused] = useState(false);
  const longerFirstCardTimeout = currentSlide === 0 && extendFirstCardTimeout;

  const timerRef = useRef<
    | {
        timer: NodeJS.Timeout | undefined;
        startTime: number;
        remaining?: number;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (currentSlide === transitionSlideId) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTransitionSlideId(-1);
    }
  }, [currentSlide, transitionSlideId]);

  const changeSlide = () => {
    const newIndex = currentSlide + 1;
    const canSlideNext = newIndex < cards.length;

    if (!canSlideNext) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTransitionSlideId(0);
    }

    scrollToSlide(canSlideNext ? newIndex : 0);
  };

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const card = cards[currentSlide];

    clearTimeout(timerRef.current?.timer);

    let slideInterval: undefined | NodeJS.Timeout;
    if (card) {
      if (longerFirstCardTimeout) {
        slideInterval = setTimeout(
          changeSlide,
          EXTENDED_FIRST_CARD_TIMEOUT * 1000,
        );
      } else {
        slideInterval = setTimeout(changeSlide, card.duration.seconds * 1000);
      }
    }

    timerRef.current = {
      timer: slideInterval,
      startTime: new Date().getTime(),
    };

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current.timer);
      }
    };
  }, [cards, currentSlide, scrollToSlide, extendFirstCardTimeout]);

  const onPause = () => {
    const card = cards[currentSlide];
    if (timerRef.current && card) {
      const cardDuration = longerFirstCardTimeout
        ? EXTENDED_FIRST_CARD_TIMEOUT * 1000
        : card.duration.seconds * 1000;
      clearTimeout(timerRef.current.timer);
      const remaining =
        cardDuration - (new Date().getTime() - timerRef.current.startTime);
      timerRef.current = {
        timer: undefined,
        startTime: timerRef.current.startTime,
        remaining,
      };
    }
    setAnimationPaused(true);
  };

  const onResume = () => {
    const card = cards[currentSlide];

    if (timerRef.current) {
      timerRef.current = {
        timer: card
          ? setTimeout(
              changeSlide,
              timerRef.current.remaining ||
                (longerFirstCardTimeout
                  ? EXTENDED_FIRST_CARD_TIMEOUT * 1000
                  : card.duration.seconds * 1000),
            )
          : undefined,
        startTime: new Date().getTime(),
      };
    }
    setAnimationPaused(false);
  };

  return (
    <SliderContainer className={className}>
      <SlidesContainer ref={sliderRef}>
        {cards.map((card, index) => {
          // On the initial mount we only want to prioritize the first image, as it's the highest priority on the server.
          // In the client we have enough time to prioritize the first two images to preload what the user will most probably unfold soon.
          // Logic and comment are taken from the product images on ADP.
          const isCloseToViewport = isInitialMount
            ? index === currentSlide
            : currentSlide - 1 <= index && currentSlide + 1 >= index;

          return (
            <li key={index} onTouchStart={onPause} onTouchEnd={onResume}>
              <CardComponent
                {...card}
                sectionIndex={sectionIndex}
                isActive={index === currentSlide}
                isCloseToViewport={isCloseToViewport}
                data-testid={`card-${index}${
                  index === currentSlide ? '-active' : ''
                }`}
              />
            </li>
          );
        })}
      </SlidesContainer>

      {cards.length > 1 && (
        <StyledProgressBarContainer>
          {cards.map((card, index) => {
            const nextActiveSlide =
              transitionSlideId >= 0 ? transitionSlideId : currentSlide;

            return (
              <SliderProgressBar
                key={index}
                durationInMs={
                  longerFirstCardTimeout
                    ? EXTENDED_FIRST_CARD_TIMEOUT * 1000
                    : card.duration.seconds * 1000
                }
                active={nextActiveSlide === index}
                done={nextActiveSlide > index}
                datatestId={`progressBar-${index}`}
                onClick={() => {
                  setTransitionSlideId(index);
                  scrollToSlide(index);
                }}
                paused={animationPaused}
              />
            );
          })}
        </StyledProgressBarContainer>
      )}
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${COLORS.Black};
`;

const CARDS_GAP = 8;

const SlidesContainer = styled.ul`
  display: flex;
  flex-wrap: nowrap;
  overflow: auto hidden;
  scroll-snap-type: x mandatory;
  touch-action: manipulation;
  box-sizing: border-box;
  height: 100%;
  padding: 56px 8px 35px 8px;
  scroll-padding: 0 8px;
  margin: 0;
  list-style: none;

  ${touchScroll};
  ${hideScrollBar};

  will-change: opacity;
  animation: ${GLOBAL_KEYFRAMES.cardsSliderAmbientFadeEnter} 500ms ease-in-out;

  @keyframes ${GLOBAL_KEYFRAMES.cardsSliderAmbientFadeEnter} {
    from {
      opacity: 0.8;
    }
    to {
      opacity: 1;
    }
  }

  > * {
    flex-shrink: 0;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    flex-basis: calc(100% - ${CARDS_GAP * 2}px);
    margin: 0 ${CARDS_GAP / 2}px;
    list-style: none;

    :first-child {
      margin-left: ${CARDS_GAP}px;
    }

    :last-child {
      margin-right: ${CARDS_GAP}px;
    }
  }
`;

const StyledProgressBarContainer = styled(ProgressBarContainer)`
  position: absolute;
  bottom: 20px;
  left: 24px;
  right: 24px;
  z-index: 1;
`;
